/**
 * Where the caster's own conjuration hangs, in camera space.
 *
 * The magic circle and the sigil traced onto it have to agree to the
 * millimetre, so the placement lives here instead of being written out twice
 * and drifting apart.
 *
 * The rig used to sit far to the right and small, on the reasoning that the
 * middle of the screen belongs to the incoming attack. That reasoning holds for
 * the attack — but it also meant a player could not read the one thing they are
 * actively performing. So: near the centre and much larger, still biased down
 * and toward the wand side. Nothing here can hide a projectile even where it
 * overlaps, because every layer is additive, the rune bands are transparent
 * through the middle, and the reticle is DOM drawn over the canvas regardless.
 *
 * The numbers below are read against the view frustum rather than eyeballed: at
 * `z`, half the visible height is `|z| * tan(fov/2)` ≈ 0.67 world units for the
 * 62° camera. A fully-built circle is `1.5 * scaleMax` across, so `scaleMax`
 * puts its radius at roughly four fifths of that half-height — big enough to be
 * the thing you are looking at, short of the edge so the band never clips.
 */
export const CAST_RIG = {
  /** Slight offset toward the wand, and below the line of fire. */
  x: 0.06,
  y: -0.17,
  /** The circle plane. */
  z: -1.12,
  /** The sigil, a hair in front, so the stroke reads as traced onto the circle. */
  trailZ: -1.10,
  /** Rig scale at rest, and at a fully-built circle. */
  scaleMin: 0.46,
  scaleMax: 0.74,
  /**
   * World units per normalised gesture unit. The recorder clamps its virtual
   * cursor to ±2.5, so a big sweep spans roughly 1.7 units here — wider than
   * the circle, which is deliberate: the sigil is the thing being judged, and a
   * stroke small enough to sit politely inside the rune bands is a stroke the
   * player cannot check against the template they registered.
   */
  trailScale: 0.34,
} as const;
